import React, { useRef, useState, useCallback, useEffect } from 'react'

import { Helmet } from 'react-helmet-async';

import {
    Box,
} from '@mui/material';

import { useNavigate, Link } from 'react-router-dom';

import DateFilter from '@inovua/reactdatagrid-community/DateFilter'

import CommonBreadcumbs from '../../common/CommonBreadcumbs';
import DataGridToolbar from '../../common/DataGridToolbar';
import DataGridTable from '../../common/DataGridTable';
import DataGridRowActionMenu from '../../common/DataGridRowActionMenu';
import CommonRestricted from '../../common/CommonRestricted';

import { icons } from '../../common/CommonIcons';
import { useDataGridParams } from '../../../hooks/useDataGridParams';
import { useUsers, userTypes, targetTypes } from '../../../hooks/api/useUsers';
import { useSystemPrompt } from '../../../hooks/useSystemPrompt';
import { useSystemRoutes, routeTypes } from '../../../hooks/useSystemRoutes';
import { useSystemHead, headTitles } from '../../../hooks/useSystemHead';
import { usePermissions, scopeIds, scopeTypes, functionTypes } from '../../../hooks/usePermissions';
import { useTranslations } from '../../../hooks/useTranslations';

export default function ListAdministrators({
    routeType
}) {

    const { t } = useTranslations({ ns: ["common"] })

    const navigate = useNavigate();
    const { promptMessage } = useSystemPrompt();
    const { list, remove } = useUsers({ userType: userTypes.admin });

    const { defineColumns, columns, filterValues, getRenderColumnLink, getRenderColumnBooleanDisplay, getRenderColumnTextDisplay } = useDataGridParams('id')
    const { isPermissionAllow } = usePermissions(routeType, scopeIds.user.id, scopeTypes.user.administrator.id)

    const [selectedIds, setSelectedIds] = useState([])
    const [refreshDataGrid, setRefreshDataGrid] = useState(0)
    const gridRef = React.createRef();
    const { getRoute } = useSystemRoutes(routeType);
    const { getHtmlHead } = useSystemHead(routeType)

    const resetDataGridTableSelection = () => {
        setSelectedIds([])
    }

    const fetchData = async (props) => {

        resetDataGridTableSelection();

        try {

            props.dataTypes = ["location_count"]

            const result = await list(props)

            return {
                data: result?.data?.administrators,
                count: result?.data?.total
            }

        } catch (error) {

            promptMessage(false, `Error getting results (${error?.message})`)

            return {
                data: [],
                count: 0
            }
        }

    }

    useEffect(() => {

        defineColumnsView()

    }, [])

    const defineColumnsView = () => {
        defineColumns([
            {
                name: 'username',
                header: t('username'),
                renderValue: ({ value, cellProps }) => {

                    if (isPermissionAllow({ functionType: functionTypes.pageView.id }) === false) {
                        return getRenderColumnTextDisplay(value)
                    }

                    let path = getRoute(`users/administrator/view/${cellProps.data.user_id}`);

                    return getRenderColumnLink(path, value)

                }
            },
            {
                name: 'firstname', header: t('firstName')
            },
            {
                name: 'lastname', header: t('lastName')
            },
            {
                name: 'contact', header: t('contact')
            },
            {
                name: 'email', header: t('email')
            },
            {
                name: 'location_count',
                header: t('location'),
                renderValue: ({ value, cellProps }) => {

                    let displayValue = parseInt(value, 10) > 0 ? `${value} ${t('location')}` : ``

                    return (
                        <>{displayValue}</>
                    )

                }
            },
        ])
    }

    const handleDelete = async () => {

        try {

            let result = await remove({ ids: selectedIds, targetType: targetTypes.admin })

            promptMessage(true, result?.message)
            setRefreshDataGrid(val => val + 1)

        } catch (error) {
            promptMessage(false, error?.message)
        }

    }

    const RenderToolbar = () => {

        const getToolbarMainMenus = () => {

            let props = [];

            if (isPermissionAllow({ functionType: functionTypes.pageNew.id })) {
                props.push({
                    displayIconName: icons.plus,
                    onClick: () => {
                        navigate(getRoute("users/administrator/new"));
                    }
                })
            }

            return props
        }

        const getActionOnClick = () => {

            let handleFunction = null

            if (isPermissionAllow({ functionType: functionTypes.remove.id })) {
                handleFunction = handleDelete
            }

            return handleFunction
        }

        return (
            <DataGridToolbar
                selectedRowCount={
                    selectedIds.length
                }
                mainMenus={
                    getToolbarMainMenus()
                }
                actionOnClickRemove={
                    getActionOnClick()
                }
                actionMenus={[]}
            />
        )
    }

    const RenderDataGridTable = () => {

        return (
            <DataGridTable
                ref={gridRef}
                refresh={refreshDataGrid}
                fetchData={fetchData}
                dataGrid={{
                    idProperty: "user_id",
                    style: {
                        minHeight: 600
                    },
                    pageSizes: [20, 50, 100],
                    defaultLimit: 10,
                    defaultSkip: 0,
                    checkboxColumn:
                        isPermissionAllow({ functionType: functionTypes.remove.id }),
                    columns,
                    defaultFilterValue: filterValues,
                    onSelectionChange: (items) => {
                        setSelectedIds(
                            items
                        )
                    }
                }}

            />
        )
    }

    return (
        <Box>
            {
                getHtmlHead({ routeType, title: headTitles.administrators })
            }

            <CommonBreadcumbs
                routeType={routeType}
                pageTitle={t('administrators')}
                breadcrumbs={[
                    {
                        displayText: t('administrators'),
                        props: {
                            // underline: "hover",
                            // href: "/"
                        }
                    }
                ]}
            />

            {
                isPermissionAllow({ functionType: functionTypes.pageList.id }) ?
                    <>
                        {
                            RenderToolbar()
                        }
                        {
                            RenderDataGridTable()
                        }
                    </>
                    :
                    (
                        <CommonRestricted />
                    )
            }

        </Box>
    )
}
